'use client';

import type { FormBookingDatePickerProps } from '@/components/common/forms/form-booking-datepicker/FormBookingDatePicker.types';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import formBookingDatepickerSchema from '@/schemas/formBookingDatepicker';
import {
  DATE_FORMAT,
  getTimeZone,
  getTimestampUTC,
  parseDate,
  toMonth
} from '@/utils/date';
import { SCHEDULE_STATUS } from '@/utils/schedule';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { CheckCircle, Circle, Loader, XCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import AlertSection from '../../alert/AlertSection';
const FormBookingDatePicker: React.FC<FormBookingDatePickerProps> = ({
  availableDates,
  organization,
  calendarPaginateNavigation,
  landing
}) => {
  // i18n text
  const { t } = useTranslation(['newBooking', 'common']);
  const Mixpanel = useMixPanel();
  const continueRef = useRef<null | HTMLButtonElement>(null);

  const addStep = useRootStore((store) => store.addStep);
  const removeStep = useRootStore((store) => store.removeStep);
  const currentStep = useRootStore((store) => store.currentStep);
  const stepServicesData = useRootStore((store) => store.stepServicesData);
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );
  const [timeIsLoading, setTimeIsLoading] = useState(false);
  const participants = stepServicesData?.participants ?? 1;

  // get the available dates and sort them
  const schedule = availableDates
    .map((date) => ({ ...date, date: parseDate(date.date) }))
    .filter((date) =>
      date.slots?.some((slot) => slot.status === SCHEDULE_STATUS.available)
    );
  const findSchedule = (date: Date) => {
    return schedule?.find(
      (value) => value?.date?.getDate() === date?.getDate()
    );
  };
  const hasSchedule = !!schedule.length;

  const defaultDate = () => {
    const isValidCurrentDate = stepServicesData?.firstReservationStartTime;
    if (isValidCurrentDate) {
      return new Date(isValidCurrentDate);
    }
    if (hasSchedule) {
      return schedule[0].date;
    }
    return undefined;
  };

  const defaultTime = () => {
    const time = stepServicesData?.firstReservationStartTime;
    if (time) {
      const parsedTime = getTimeZone(time, organization.tz);
      const slots = findSchedule(parsedTime)?.slots;
      return slots?.find((slot) =>
        slot.from.startsWith(parsedTime.getHours().toString())
      );
    }
  };

  // Initializing useForm hook with the schema and default values
  const form = useForm<z.infer<typeof formBookingDatepickerSchema>>({
    resolver: zodResolver(formBookingDatepickerSchema),
    defaultValues: {
      date: defaultDate(),
      time: defaultTime()
    }
  });

  const dateValue = form.getValues('date');

  const hasUnavailableSchedule = findSchedule(dateValue)?.slots.some(
    (slot) => slot.status !== SCHEDULE_STATUS.available
  );

  const handleDateChange = () => {
    // Hide the time field before showing it again to force re-render
    setTimeIsLoading(true);
    setTimeout(() => {
      setTimeIsLoading(false);
    }, 300); // Using a delay of 0ms for quick toggle
  };

  // Function to handle form submission
  function onSubmit(values: z.infer<typeof formBookingDatepickerSchema>) {
    const time = values.time.from; // Gets the first part of the time range
    const firstReservationStartTime = getTimestampUTC({
      date: values.date,
      time,
      tz: organization.tz
    });
    loadStepServicesData({ firstReservationStartTime });
    addStep(currentStep);
  }

  const scrollToContinue = (ref: React.RefObject<HTMLButtonElement>) => {
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <h2 className="font-bold mb-6">{t('stepDateTitle')}</h2>
        <div className="bg-accent rounded-sm py-2 px-5 flex flex-row align-middle justify-between">
          <h3 className="font-bold ">{stepServicesData?.service?.name}</h3>
          <p className="">
            {participants} {t('stepParticipants.participant')}
            {participants > 1 ? 's' : ''}
          </p>
        </div>
        <div className="my-8 flex flex-col gap-4">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl className="max-w-lg my-0 mx-auto">
                    <Calendar
                      classNames={{
                        row: 'flex w-full mt-2 justify-around',
                        head_row: 'flex justify-around',
                        months:
                          'flex flex-col space-y-4 sm:space-x-4 sm:space-y-0'
                      }}
                      mode="single"
                      selected={field.value}
                      onSelect={(e) => {
                        if (e) {
                          Mixpanel.track('select date', {
                            screen: 'New Booking',
                            dateSelected: format(e, DATE_FORMAT) //TODO: revisar fecha formatear a fecha con tz de la org
                          });
                          form.reset({
                            date: e,
                            time: undefined
                          });
                          handleDateChange();
                        }
                      }}
                      fromMonth={new Date()}
                      toMonth={toMonth}
                      onMonthChange={(date) => {
                        const curretMonthPaginated =
                          calendarPaginateNavigation.date;

                        if (curretMonthPaginated.getTime() < date.getTime()) {
                          calendarPaginateNavigation.goToNextMonth();
                        } else {
                          calendarPaginateNavigation.goToPreviousMonth();
                        }
                        form.reset({
                          date: undefined,
                          time: undefined
                        });
                      }}
                      defaultMonth={defaultDate()}
                      disabled={(date) => {
                        if (!availableDates) return true;
                        return !schedule.some(
                          (d) => date.getTime() === d.date.getTime()
                        );
                      }}
                      initialFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          {timeIsLoading && !!form.getValues().date ? (
            <div>
              <FormLabel className="font-bold">
                {t('stepDateRadioTitle')}
              </FormLabel>
              <div className="flex items-center justify-center my-4 min-h-24">
                <Loader className="animate-spin" />
              </div>
            </div>
          ) : (
            <FormField
              control={form.control}
              name="time"
              render={({ field }) => {
                const slots = dateValue
                  ? findSchedule(dateValue)?.slots
                  : undefined;
                return slots ? (
                  <FormItem>
                    <FormLabel className="font-bold">
                      {t('stepDateRadioTitle')}
                      <p className="font-normal mt-2 mb-4">
                        {t('stepDateRadioDescripcion')}
                      </p>
                    </FormLabel>
                    <FormControl>
                      <FormItem className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 space-y-0 gap-3">
                        {slots?.map((slot) => {
                          const from = slot.from;
                          const to = slot.to;
                          const isSelected = field.value?.from === from;
                          const isAvailable =
                            slot.status === SCHEDULE_STATUS.available;
                          const value = `${from}-${to}`;
                          if (!isAvailable) {
                            return (
                              <Button
                                type="button"
                                key={value}
                                className="flex flex-col items-start h-20 border-red-400"
                                variant={'outline'}
                                disabled
                              >
                                <div className="flex">
                                  <XCircle
                                    className="text-red-400"
                                    width={20}
                                  />
                                  <p className="text-base ml-3">{value}</p>
                                </div>
                                <p className="text-red-600">
                                  {slot.statusDetail
                                    ? slot.statusDetail
                                    : t('unAvailable')}
                                </p>
                              </Button>
                            );
                          }
                          return (
                            <Button
                              type="button"
                              key={value}
                              onClick={() => {
                                field.onChange(slot);
                                Mixpanel.track('select time', {
                                  screen: 'New Booking',
                                  timeSelected: value
                                });
                                scrollToContinue(continueRef);
                              }}
                              variant={isSelected ? 'default' : 'outline'}
                              className="flex justify-start h-20"
                            >
                              <div>
                                {isSelected ? (
                                  <CheckCircle width={20} />
                                ) : (
                                  <Circle
                                    className="text-zinc-400"
                                    width={20}
                                  />
                                )}
                              </div>
                              <p className="text-base ml-3">{value}</p>
                            </Button>
                          );
                        })}
                      </FormItem>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                ) : (
                  <></>
                );
              }}
            />
          )}
        </div>
        <div className="mb-8 space-y-2">
          {!hasSchedule && (
            <AlertSection variant="error" text={t('alertAvailablilityDates')} />
          )}
          {hasUnavailableSchedule && (
            <AlertSection variant="error" text={t('unAvailableMessage')} />
          )}
          <AlertSection variant="warning" text={t('messageAvailability')} />
          <AlertSection
            variant="info"
            text={t('messageTimezone', { tz: organization.tz })}
          />
        </div>
        {!landing ? (
          <div className="flex flex-col gap-3">
            <Button
              ref={continueRef}
              onClick={() =>
                Mixpanel.track('press continue button', {
                  screen: 'New Booking'
                })
              }
              type="submit"
            >
              {t('common:continue')}
            </Button>
            <Button
              onClick={() => {
                Mixpanel.track('press go back', {
                  screen: 'New Booking'
                });
                removeStep(currentStep);
                loadStepServicesData({
                  firstReservationStartTime: undefined
                });
              }}
              variant={'outline'}
            >
              {t('common:goBack')}
            </Button>
          </div>
        ) : null}
      </form>
    </Form>
  );
};

export default FormBookingDatePicker;
